import type { Dispatch } from '@reduxjs/toolkit';
import type {
  ICompanyClientState,
  IGetCompanyClientsQueryParams,
  INewAndUpdateClientDto,
  INewAndUpdateContactPersonDto,
} from 'src/legacy/@types/client';
import type { IVaultDocument } from 'src/legacy/@types/common';

import { createSlice } from '@reduxjs/toolkit';
import axios from 'src/legacy/utils/axios';

// ----------------------------------------------------------------------

const initialState: ICompanyClientState = {
  actionStatus: {
    contactPerson: {
      create: null,
      delete: 'none',
      update: null,
    },
    create: 'none',
    delete: 'none',
    document: {
      create: 'none',
      delete: 'none',
      update: 'none',
      upload: 'none',
    },
    update: 'none',
  },
  companyClients: null,
  contactPersons: null,
  error: null,
  isLoading: false,
  selectedClient: null,
  selectedContactPerson: null,
  vault: {
    count: 0,
    next: '',
    previous: '',
    results: [],
  },
};

const slice = createSlice({
  initialState,
  name: 'client',
  reducers: {
    // CLEAR ERROR
    clearError(state) {
      state.error = '';
    },

    createClientDocumentFailed(state) {
      state.actionStatus = {
        ...state.actionStatus,
        document: {
          ...state.actionStatus.document,
          create: 'failed',
        },
      };
    },

    // CREATE NEW CLIENT DOCUMENT
    createClientDocumentStart(state) {
      state.actionStatus = {
        ...state.actionStatus,
        document: {
          ...state.actionStatus.document,
          create: 'loading',
        },
      };
    },

    createClientDocumentSuccess(state, action) {
      state.actionStatus = {
        ...state.actionStatus,
        document: {
          ...state.actionStatus.document,
          create: 'complete',
        },
      };
      state.error = null;

      if (state.selectedClient) {
        state.selectedClient = {
          ...state.selectedClient,
          documents: [
            ...(state.selectedClient?.documents || []),
            action.payload,
          ],
        };
      }
    },

    createClientVaultDocumentFailed(state) {
      state.actionStatus = {
        ...state.actionStatus,
        document: {
          ...state.actionStatus.document,
          create: 'failed',
        },
      };
    },

    createClientVaultDocumentStart(state) {
      state.actionStatus = {
        ...state.actionStatus,
        document: {
          ...state.actionStatus.document,
          create: 'loading',
        },
      };
    },

    createClientVaultDocumentSuccess(state, action) {
      state.actionStatus = {
        ...state.actionStatus,
        document: {
          ...state.actionStatus.document,
          create: 'complete',
        },
      };
      state.error = null;
    },

    // CREATE NEW COMPANY CLIENT
    createCompanyClientSuccess(state, action) {
      state.actionStatus = {
        ...state.actionStatus,
        create: 'complete',
      };
      state.error = null;
      if (state.companyClients) {
        state.companyClients = [...state.companyClients, action.payload];
      }
    },

    createContactPersonFailed(state, action) {
      state.actionStatus = {
        ...state.actionStatus,
        contactPerson: {
          ...state.actionStatus.contactPerson,
          create: [
            ...(state.actionStatus.contactPerson.update || []),
            action.payload,
          ],
        },
      };
    },

    // CREATE NEW CONTACT PERSON
    createContactPersonStart(state) {
      state.actionStatus = {
        ...state.actionStatus,
        contactPerson: {
          ...state.actionStatus.contactPerson,
          create: null,
        },
      };
    },

    createContactPersonSuccess(state, action) {
      state.actionStatus = {
        ...state.actionStatus,
        contactPerson: {
          ...state.actionStatus.contactPerson,
          create: null,
        },
      };
      state.error = null;
      state.contactPersons = [...(state.contactPersons || []), action.payload];
    },

    deleteClientDocumentFailed(state) {
      state.actionStatus = {
        ...state.actionStatus,
        document: {
          ...state.actionStatus.document,
          delete: 'failed',
        },
      };
    },

    // UPDATE CLIENT DOCUMENT
    deleteClientDocumentStart(state) {
      state.actionStatus = {
        ...state.actionStatus,
        document: {
          ...state.actionStatus.document,
          delete: 'loading',
        },
      };
    },

    deleteClientDocumentSuccess(state, action) {
      state.actionStatus = {
        ...state.actionStatus,
        document: {
          ...state.actionStatus.document,
          delete: 'complete',
        },
      };
      state.error = null;

      if (state.selectedClient) {
        state.selectedClient = {
          ...state.selectedClient,
          documents: state.selectedClient.documents.filter(
            (doc) => doc.id !== action.payload.documentId
          ),
        };
      }
    },

    deleteClientVaultDocumentFailed(state) {
      state.actionStatus = {
        ...state.actionStatus,
        document: {
          ...state.actionStatus.document,
          delete: 'failed',
        },
      };
    },

    // DELETE COMPANY VAULT FILE
    deleteClientVaultDocumentStart(state) {
      state.actionStatus = {
        ...state.actionStatus,
        document: {
          ...state.actionStatus.document,
          delete: 'loading',
        },
      };
    },

    deleteClientVaultDocumentSuccess(state, action) {
      state.actionStatus = {
        ...state.actionStatus,
        document: {
          ...state.actionStatus.document,
          delete: 'complete',
        },
      };
      state.error = null;
    },

    // DELETE COMPANY CLIENT
    deleteCompanyClientSuccess(state, action) {
      state.actionStatus = {
        ...state.actionStatus,
        delete: 'complete',
      };
      if (state.companyClients) {
        state.companyClients = state.companyClients.filter(
          (client) => client.id !== action.payload.clientId
        );
      }
    },

    deleteContactPersonFailed(state) {
      state.actionStatus = {
        ...state.actionStatus,
        contactPerson: {
          ...state.actionStatus.contactPerson,
          delete: 'failed',
        },
      };
    },

    // DELETE CONTACT PERSON
    deleteContactPersonStart(state) {
      state.actionStatus = {
        ...state.actionStatus,
        contactPerson: {
          ...state.actionStatus.contactPerson,
          delete: 'loading',
        },
      };
    },

    deleteContactPersonSuccess(state, action) {
      state.actionStatus = {
        ...state.actionStatus,
        contactPerson: {
          ...state.actionStatus.contactPerson,
          delete: 'complete',
        },
      };
      if (state.contactPersons) {
        state.contactPersons = state.contactPersons.filter(
          (person) => person.id !== action.payload.personId
        );
      }
    },

    // GET CLIENT DOCUMENT LIST
    getClientDocumentListSuccess(state, action) {
      // Todo
    },

    // GET CLIENT DOCUMENT
    getClientDocumentSuccess(state, action) {
      // Todo
    },

    // VAULT REDUCER ACTIONS //
    // CREATE NEW CLIENT VAULT FILE
    getClientVaultDocumentSuccess(state, action) {
      state.isLoading = false;
      state.vault = action.payload;
    },

    // GET COMPANY CLIENTS
    getCompanyClientsSuccess(state, action) {
      state.isLoading = false;
      state.companyClients = action.payload;
    },

    // GET COMPANY CLIENT
    getCompanyClientSuccess(state, action) {
      state.isLoading = false;
      state.selectedClient = action.payload;
    },

    // GET CONTACT PERSON LIST
    getContactPersonsSuccess(state, action) {
      state.isLoading = false;
      state.contactPersons = action.payload;
    },

    // GET CONTACT PERSON
    getContactPersonSuccess(state, action) {
      state.isLoading = false;
      state.selectedContactPerson = action.payload;
    },

    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // UPDATE ACTION STATUS
    updateActionState(state, action) {
      state.actionStatus = {
        ...state.actionStatus,
        ...action.payload,
      };
    },

    updateClientDocumentFailed(state) {
      state.actionStatus = {
        ...state.actionStatus,
        document: {
          ...state.actionStatus.document,
          update: 'failed',
        },
      };
    },

    // UPDATE CLIENT DOCUMENT
    updateClientDocumentStart(state) {
      state.actionStatus = {
        ...state.actionStatus,
        document: {
          ...state.actionStatus.document,
          update: 'loading',
        },
      };
    },

    updateClientDocumentSuccess(state, action) {
      state.actionStatus = {
        ...state.actionStatus,
        document: {
          ...state.actionStatus.document,
          update: 'complete',
        },
      };
      state.error = null;

      // Todo
    },

    updateClientVaultFolderFailed(state) {
      state.actionStatus = {
        ...state.actionStatus,
        document: {
          ...state.actionStatus.document,
          update: 'failed',
        },
      };
    },

    // UPDATE COMPANY DOCUMENT
    updateClientVaultFolderStart(state) {
      state.actionStatus = {
        ...state.actionStatus,
        document: {
          ...state.actionStatus.document,
          update: 'loading',
        },
      };
    },

    updateClientVaultFolderSuccess(state, action) {
      state.actionStatus = {
        ...state.actionStatus,
        document: {
          ...state.actionStatus.document,
          update: 'complete',
        },
      };
      state.error = null;
      const list = state.vault.results.map((doc) =>
        doc.id === action.payload.id ? action.payload : doc
      );

      state.vault = {
        ...state.vault,
        results: list,
      };
    },

    // UPDATE COMPANY CLIENT
    updateCompanyClientSuccess(state, action) {
      state.actionStatus = {
        ...state.actionStatus,
        update: 'complete',
      };
      state.error = null;
      if (state.companyClients) {
        state.companyClients = state.companyClients.map((client) => {
          if (client.id === action.payload.id) {
            return action.payload;
          }
          return client;
        });
      }
    },

    updateContactPersonFailed(state, action) {
      state.actionStatus = {
        ...state.actionStatus,
        contactPerson: {
          ...state.actionStatus.contactPerson,
          update: [
            ...(state.actionStatus.contactPerson.update || []),
            action.payload,
          ],
        },
      };
    },

    // UPDATE CONTACT PERSON
    updateContactPersonStart(state) {
      state.actionStatus = {
        ...state.actionStatus,
        contactPerson: {
          ...state.actionStatus.contactPerson,
          update: null,
        },
      };
    },

    updateContactPersonSuccess(state, action) {
      state.actionStatus = {
        ...state.actionStatus,
        contactPerson: {
          ...state.actionStatus.contactPerson,
          update: null,
        },
      };
      state.error = null;
      if (state.contactPersons) {
        state.contactPersons = state.contactPersons.map((person) => {
          if (person.id === action.payload.id) {
            return action.payload;
          }
          return person;
        });
      }
    },

    uploadClientVaultDocumentFailed(state) {
      state.actionStatus = {
        ...state.actionStatus,
        document: {
          ...state.actionStatus.document,
          upload: 'failed',
        },
      };
    },

    uploadClientVaultDocumentStart(state) {
      state.actionStatus = {
        ...state.actionStatus,
        document: {
          ...state.actionStatus.document,
          upload: 'loading',
        },
      };
    },

    uploadClientVaultDocumentSuccess(state, action) {
      state.actionStatus = {
        ...state.actionStatus,
        document: {
          ...state.actionStatus.document,
          upload: 'complete',
        },
      };
      state.error = null;
    },
  },
});

// Reducer
export default slice.reducer;

// Actions
export const {
  clearError,
  createCompanyClientSuccess,
  getCompanyClientSuccess,
  getContactPersonsSuccess,
  updateActionState,
} = slice.actions;

// ----------------------------------------------------------------------
// --------------           Company Client Actions         --------------
// ----------------------------------------------------------------------

export const getCompanyClients = (params: IGetCompanyClientsQueryParams) => {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      const response = await axios.get(`/care/clients/`, { params });
      let allResults: any[] = response.data.results;

      if (
        response.data.count > allResults.length ||
        response.data.next !== ''
      ) {
        await Promise.all(
          Array(Math.ceil(response.data.count / 20) - 1)
            .fill(0)
            .map(async (_, index) => {
              const response2 = await axios.get(`/care/clients/`, {
                params: { ...params, page: index + 2 },
              });
              allResults = [...allResults, ...response2.data.results];
            })
        );
      }

      dispatch(slice.actions.getCompanyClientsSuccess(allResults));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------

export const createCompanyClient = (
  clientId: string,
  newClient: INewAndUpdateClientDto
) => {
  return async (dispatch: Dispatch<any>) => {
    dispatch(
      slice.actions.updateActionState({ create: 'loading', error: null })
    );
    try {
      const response = await axios.post(`/care/clients/?company=${clientId}`, {
        ...newClient,
        contact_persons: [],
      });
      if (response.data.id && newClient.contact_persons.length > 0) {
        newClient.contact_persons.forEach((person) => {
          dispatch(createContactPerson(response.data.id, person));
        });
      }
      dispatch(slice.actions.createCompanyClientSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.updateActionState({ create: 'failed' }));
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------

export const getCompanyClient = (clientId: number) => {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(`/care/clients/${clientId}/`);
      dispatch(slice.actions.getCompanyClientSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------

export const updateCompanyClient = (
  clientId: number,
  updatedClient: INewAndUpdateClientDto
) => {
  return async (dispatch: Dispatch<any>) => {
    dispatch(
      slice.actions.updateActionState({ error: null, update: 'loading' })
    );
    try {
      if (
        updatedClient.contact_persons &&
        updatedClient.contact_persons?.length > 0
      ) {
        updatedClient.contact_persons.forEach((person) => {
          if (person.client) {
            if (person.isDeleted) {
              dispatch(deleteContactPerson(clientId, person.id));
            }
            if (person.isModified) {
              dispatch(updateContactPerson(clientId, person.id, person));
            }
          } else {
            dispatch(createContactPerson(clientId, person));
          }
        });
      }
      const response = await axios.put(`/care/clients/${clientId}/`, {
        ...updatedClient,
        contact_persons: [],
      });
      dispatch(slice.actions.updateCompanyClientSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.updateActionState({ update: 'failed' }));
      dispatch(slice.actions.hasError(error));
    }
  };
};

export const patchCompanyClient = (
  clientId: number,
  payload: Partial<INewAndUpdateClientDto>
) => {
  return async (dispatch: Dispatch) => {
    dispatch(
      slice.actions.updateActionState({ error: null, update: 'loading' })
    );
    try {
      const response = await axios.patch(`/care/clients/${clientId}/`, payload);
      dispatch(slice.actions.updateCompanyClientSuccess(response.data));
      dispatch(slice.actions.updateActionState({ update: 'none' }));
    } catch (error) {
      dispatch(slice.actions.updateActionState({ update: 'failed' }));
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------

export const deleteCompanyClient = (clientId: number) => {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.updateActionState({ delete: 'loading' }));
    try {
      await axios.delete(`/care/clients/${clientId}/`);
      dispatch(slice.actions.deleteCompanyClientSuccess({ clientId }));
    } catch (error) {
      dispatch(slice.actions.updateActionState({ delete: 'failed' }));
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------
// --------------           Contact Person Actions         --------------
// ----------------------------------------------------------------------

export const getContactPersons = (clientId: number) => {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(
        `/care/clients/${clientId}/contact-persons/`
      );
      dispatch(slice.actions.getContactPersonsSuccess(response.data.results));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------

export const createContactPerson = (
  clientId: number,
  newContactPerson: INewAndUpdateContactPersonDto
) => {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.createContactPersonStart());
    try {
      const response = await axios.post(
        `/care/clients/${clientId}/contact-persons/`,
        newContactPerson
      );
      dispatch(slice.actions.createContactPersonSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.createContactPersonFailed(error));
    }
  };
};

// ----------------------------------------------------------------------

export const getContactPerson = (clientId: number, contactPersonId: number) => {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(
        `/care/clients/${clientId}/contact-persons/${contactPersonId}/`
      );
      dispatch(slice.actions.getContactPersonSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------

export const updateContactPerson = (
  clientId: number,
  contactPersonId: number,
  updatedContactPerson: INewAndUpdateContactPersonDto
) => {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.updateContactPersonStart());
    try {
      const response = await axios.put(
        `/care/clients/${clientId}/contact-persons/${contactPersonId}/`,
        updatedContactPerson
      );
      dispatch(slice.actions.updateContactPersonSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.updateContactPersonFailed(error));
    }
  };
};

// ----------------------------------------------------------------------

export const deleteContactPerson = (
  clientId: number,
  contactPersonId: number
) => {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.deleteContactPersonStart());
    try {
      await axios.delete(
        `/care/clients/${clientId}/contact-persons/${contactPersonId}`
      );
      dispatch(slice.actions.deleteContactPersonSuccess({ contactPersonId }));
    } catch (error) {
      dispatch(slice.actions.deleteContactPersonFailed());
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------
// --------------          Client Document Actions         --------------
// ----------------------------------------------------------------------

export const getClientDocumentList = (
  clientId: string,
  params: IGetCompanyClientsQueryParams
) => {
  return async (dispatch: Dispatch) => {
    try {
      const response = await axios.get(`/care/clients/${clientId}/documents/`, {
        params,
      });
      dispatch(
        slice.actions.getClientDocumentListSuccess(response.data.results)
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------

export const createClientDocument = (clientId: string, data: FormData) => {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.createClientDocumentStart());
    try {
      const response = await axios.post(
        `/care/clients/${clientId}/documents/`,
        data,
        {
          headers: {
            'content-type': 'multipart/form-data',
          },
        }
      );
      dispatch(slice.actions.createClientDocumentSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.createClientDocumentFailed());
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------

export const getClientDocument = (clientId: string, documentId: string) => {
  return async (dispatch: Dispatch) => {
    try {
      const response = await axios.get(
        `/care/clients/${clientId}/documents/${documentId}/`
      );
      dispatch(slice.actions.getClientDocumentSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------

export const updateClientDocument = (
  clientId: string,
  documentId: string,
  data: FormData
) => {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.updateClientDocumentStart());
    try {
      const response = await axios.put(
        `/care/clients/${clientId}/documents/${documentId}/`,
        data,
        {
          headers: { 'Content-Type': 'application/json' },
        }
      );
      dispatch(slice.actions.updateClientDocumentSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.updateClientDocumentFailed());
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------

export const deleteClientDocument = (clientId: string, documentId: number) => {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.deleteClientDocumentStart());
    try {
      await axios.delete(`/care/clients/${clientId}/documents/${documentId}/`);
      dispatch(slice.actions.deleteClientDocumentSuccess({ documentId }));
    } catch (error) {
      dispatch(slice.actions.deleteClientDocumentFailed());
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------
// --------------          Client Vault Actions        --------------
// ----------------------------------------------------------------------

export const getClientVault = (clientId: string) => {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(`/care/clients/${clientId}/vault/`);
      const finalList = response.data.results.map((doc: IVaultDocument) => {
        return {
          ...doc,
          id: `${doc.type}_${doc.id}`,
        };
      });
      dispatch(
        slice.actions.getClientVaultDocumentSuccess({
          ...response.data,
          results: finalList,
        })
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------

export const uploadClientVaultFile = (clientId: string, data: FormData) => {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.uploadClientVaultDocumentStart());
    try {
      const response = await axios.post(
        `/care/clients/${clientId}/vault/files/`,
        data,
        {
          headers: {
            'content-type': 'multipart/form-data',
          },
        }
      );
      dispatch(slice.actions.uploadClientVaultDocumentSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.uploadClientVaultDocumentFailed());
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------

export const deleteClientVaultFile = (clientId: string, documentId: number) => {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.deleteClientVaultDocumentStart());
    try {
      await axios.delete(
        `/care/clients/${clientId}/vault/files/${documentId}/`
      );
      dispatch(
        slice.actions.deleteClientVaultDocumentSuccess({
          documentId,
          fileType: 'file',
        })
      );
    } catch (error) {
      dispatch(slice.actions.deleteClientVaultDocumentFailed());
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------

export const getClientVaultFolderContents = (
  clientId: string,
  folderId: string
) => {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(
        `/care/clients/${clientId}/vault/folders/${folderId}/contents/`
      );
      const finalList = response.data.results.map((doc: IVaultDocument) => {
        return {
          ...doc,
          id: `${doc.type}_${doc.id}`,
        };
      });
      dispatch(
        slice.actions.getClientVaultDocumentSuccess({
          ...response.data,
          results: finalList,
        })
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------

export const createClientVaultFolder = (clientId: string, data: any) => {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.createClientVaultDocumentStart());
    try {
      const response = await axios.post(
        `/care/clients/${clientId}/vault/folders/`,
        data,
        {
          headers: { 'Content-Type': 'application/json' },
        }
      );
      dispatch(slice.actions.createClientVaultDocumentSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.createClientVaultDocumentFailed());
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------

export const updateClientVaultFolder = (
  clientId: string,
  folderId: string,
  data: any
) => {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.updateClientVaultFolderStart());
    try {
      const response = await axios.put(
        `/care/clients/${clientId}/vault/folders/${folderId}/`,
        data,
        {
          headers: { 'Content-Type': 'application/json' },
        }
      );
      dispatch(slice.actions.updateClientVaultFolderSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.updateClientVaultFolderFailed());
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------

export const deleteClientVaultFolder = (
  clientId: string,
  documentId: number
) => {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.deleteClientVaultDocumentStart());
    try {
      await axios.delete(
        `/care/clients/${clientId}/vault/folders/${documentId}/`
      );
      dispatch(
        slice.actions.deleteClientVaultDocumentSuccess({
          documentId,
          fileType: 'folder',
        })
      );
    } catch (error) {
      dispatch(slice.actions.deleteClientVaultDocumentFailed());
      dispatch(slice.actions.hasError(error));
    }
  };
};
